import { ApiPost, ApiGet } from './Common.ts'
import { GetWebConnectorUrl } from '../stores/SettingsStore.ts'
import type { 
  IPwsProfileGeneralGetRequest, 
  IPwsProfileGeneralGetResponse, 
  IPwsProfileGeneralSaveRequest, 
  IPwsProfileGeneralSaveResponse,
  IPwsProfileAccountInfoSaveRequest, 
  IPwsProfileAccountInfoSaveResponse,
  IPwsProfileAddressGetRequest,
  IPwsProfileAddressGetResponse,
  IPwsProfileAddressSaveRequest,
  IPwsProfileAddressSaveResponse,
  IPwsProfileAddressDeleteRequest,
  IPwsProfileAddressDeleteResponse,
  IPwsProfileBusinessOptInGetRequest,
  IPwsProfileBusinessOptInGetResponse,
  IPwsProfileBusinessOptInSaveRequest,
  IPwsProfileBusinessOptInSaveResponse,
  IShareCddCodeGetListRequest,
  IShareCddCodeGetListResponse,
  IDataPrivacyRequestCreateRequest,
  IDataPrivacyRequestCreateResponse,
  ITaxPayerGetListRequest,
  ITaxPayerGetListResponse
} from '@/types/AccountInfoTypes'

/**
 * Gets the general profile information for a given BEE number
 * @param request Object containing the beeNumber
 * @returns Promise with the profile data response
 */
export async function PwsProfileGeneralGet(request: IPwsProfileGeneralGetRequest): Promise<IPwsProfileGeneralGetResponse> {
  const url = await GetWebConnectorUrl('PwsProfileGeneralGet')
  return ApiPost(url, request)
}

/**
 * Saves general profile information including password changes
 * @param request Object containing profile data and optional password change
 * @returns Promise with the save response
 */
export async function PwsProfileGeneralSave(request: IPwsProfileGeneralSaveRequest): Promise<IPwsProfileGeneralSaveResponse> {
  const url = await GetWebConnectorUrl('PwsProfileGeneralSave')
  return ApiPost(url, request)
}

/**
 * Saves account info specifically for password changes
 * @param request Object containing BeeNumber, OldPassword, and NewPassword
 * @returns Promise with the save response
 */
export async function PwsProfileAccountInfoSave(request: IPwsProfileAccountInfoSaveRequest): Promise<IPwsProfileAccountInfoSaveResponse> {
  const url = await GetWebConnectorUrl('PwsProfileAccountInfoSave')
  return ApiPost(url, request)
}

/**
 * Gets the address list for a given BEE number
 * @param request Object containing the beeNumber
 * @returns Promise with the address list response
 */
export async function PwsProfileAddressGet(request: IPwsProfileAddressGetRequest): Promise<IPwsProfileAddressGetResponse> {
  const url = await GetWebConnectorUrl('PwsProfileAddressGet')
  return ApiPost(url, request)
}

/**
 * Saves an address for a given BEE number
 * @param request Object containing beeNumber, addressId (for updates), address data, and default flags
 * @returns Promise with the save response
 */
export async function PwsProfileAddressSave(request: IPwsProfileAddressSaveRequest): Promise<IPwsProfileAddressSaveResponse> {
  const url = await GetWebConnectorUrl('PwsProfileAddressSave')
  return ApiPost(url, request)
}

/**
 * Deletes an address for a given BEE number
 * @param request Object containing beeNumber and addressId
 * @returns Promise with the delete response
 */
export async function PwsProfileAddressDelete(request: IPwsProfileAddressDeleteRequest): Promise<IPwsProfileAddressDeleteResponse> {
  const url = await GetWebConnectorUrl('PwsProfileAddressDelete')
  return ApiPost(url, request)
}

/**
 * Gets the marketing opt-in list for a given BEE number
 * @param request Object containing the beeNumber
 * @returns Promise with the opt-in list response
 */
export async function PwsProfileBusinessOptInGetList(request: IPwsProfileBusinessOptInGetRequest): Promise<IPwsProfileBusinessOptInGetResponse> {
  const url = await GetWebConnectorUrl('PwsProfileBusinessOptInGetList')
  return ApiPost(url, request)
}

/**
 * Saves marketing opt-in preferences for a given BEE number
 * @param request Object containing beeNumber and opt-in updates
 * @returns Promise with the save response
 */
export async function PwsProfileBusinessOptInSave(request: IPwsProfileBusinessOptInSaveRequest): Promise<IPwsProfileBusinessOptInSaveResponse> {
  const url = await GetWebConnectorUrl('PwsProfileBusinessOptInSave')
  return ApiPost(url, request)
}

export async function ShareCddCodeGetList(request: IShareCddCodeGetListRequest): Promise<IShareCddCodeGetListResponse> {
  const url = await GetWebConnectorUrl('ShareCddCodeGetList')
  return ApiPost(url, request)
}

/**
 * Creates a data privacy request for a given BEE number
 * @param request Object containing beeNumber and privacyRequestDate
 * @returns Promise with the privacy request response
 */
export async function DataPrivacyRequestCreate(request: IDataPrivacyRequestCreateRequest): Promise<IDataPrivacyRequestCreateResponse> {
  const url = await GetWebConnectorUrl('DataPrivacyRequestCreate')
  return ApiPost(url, request)
}

/**
 * Gets the tax payer/government ID list for a given BEE number
 * @param request Object containing the beeNumber
 * @returns Promise with the tax payer list response
 */
export async function TaxPayerGetList(request: ITaxPayerGetListRequest): Promise<ITaxPayerGetListResponse> {
  const url = await GetWebConnectorUrl('TaxPayerGetList')
  return ApiGet(url, request)
}